import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Footer } from 'components/Footer';
import Header from 'components/Header';
import { useToast } from 'components/toast/ToastProvider';
import { PATHS } from 'routes/paths';
import { authService } from 'services/auth/authService';
import { userService } from 'services/user/userService';
import { authStorage } from 'utils/storage';
import { getErrorMessage } from 'utils/http';
import { hasDashboardAccess } from 'utils/subscription';
import { isValidEmail } from 'utils/validation';

type Errors = {
  email?: string;
  password?: string;
};

export default function LoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Errors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { showError, showSuccess } = useToast();

  const stripedBg = {
    backgroundImage:
      'repeating-linear-gradient(-45deg, var(--bg-stripe), var(--bg-stripe) 2px, var(--bg-body) 2px, var(--bg-body) 10px)',
    backgroundColor: 'var(--bg-body)',
  };

  const validate = () => {
    const newErrors: Errors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData((current) => ({ ...current, [name]: value }));

    if (errors[name as keyof Errors]) {
      setErrors((current) => ({ ...current, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validate()) {
      showError('Please fix the highlighted login fields.');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await authService.login(formData);
      authStorage.setToken(response.data.token);
      authStorage.setUser(response.data.user);

      const dashboardResponse = await userService.getDashboard();
      const hasPaymentAccess = hasDashboardAccess(dashboardResponse.data);
      authStorage.setSubscriptionActive(hasPaymentAccess);

      showSuccess(response.message || 'Login successful');
      navigate(hasPaymentAccess ? PATHS.account : PATHS.pricing);
    } catch (error) {
      showError(getErrorMessage(error, 'Unable to log in right now.'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      style={stripedBg}
      className="min-h-screen w-full py-2 px-2 md:pt-8 md:pb-8 sm:px-8 lg:px-12 flex justify-center font-sans text-slate-900 overflow-hidden"
    >
      <div
        className="w-full bg-white rounded-[2.5rem] overflow-hidden border border-gray-200 flex flex-col shadow-sm"
        style={{ boxShadow: 'var(--shadow-box)' }}
      >
        <Header />

        <main className="flex-grow flex items-center justify-center py-24 px-4">
          <div className="w-full max-w-md bg-white rounded-3xl border border-gray-200 p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
            <div className="text-center mb-10">
              <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-3">Welcome Back</h1>
              <p className="text-slate-500 font-medium">Log in to continue learning with us.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div className="space-y-2">
                <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                  Email <span className="text-slate-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  className={`w-full bg-white border rounded-xl px-4 py-3.5 text-slate-900 placeholder-gray-400 focus:outline-none focus:ring-1 transition-all ${
                    errors.email
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-200 focus:border-slate-800 focus:ring-slate-800'
                  }`}
                />
                {errors.email && <p className="text-xs font-bold text-red-500 mt-1">{errors.email}</p>}
              </div>

              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    Password <span className="text-slate-400">*</span>
                  </label>
              
                </div>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  className={`w-full bg-white border rounded-xl px-4 py-3.5 text-slate-900 focus:outline-none focus:ring-1 transition-all tracking-widest ${
                    errors.password
                      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                      : 'border-gray-200 focus:border-slate-800 focus:ring-slate-800'
                  }`}
                />
                {errors.password && <p className="text-xs font-bold text-red-500 mt-1">{errors.password}</p>}
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#2b3543] text-white py-4 rounded-xl font-bold tracking-wide hover:bg-[#1e2631] transition-colors shadow-sm disabled:opacity-70 disabled:cursor-not-allowed flex justify-center items-center h-[56px]"
                >
                  {isSubmitting ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : (
                    'LOG IN'
                  )}
                </button>
              </div>
            </form>

            <div className="mt-8 text-center">
              <p className="text-sm font-medium text-slate-500">
                Don&apos;t have an account?{' '}
                <Link to={PATHS.signup} className="font-bold text-slate-900 hover:underline transition-colors">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
}
